// File: frontend/src/views/private/PolicyDetails.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/apiService';
import PageHeader from '../../components/layout/PageHeader';
import PageContainer from '../../components/layout/PageContainer';
import PolicyCommandCenter from '../../components/policy/PolicyCommandCenter';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import { 
  FileText, 
  AlertCircle,
  Loader2,
  Trash2
} from 'lucide-react';
import styles from './PolicyDetails.module.css';

const PolicyDetails = () => {
  const { fileId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [fileData, setFileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStartingPolicyChat, setIsStartingPolicyChat] = useState(false);
  const [isStartingClaimsChat, setIsStartingClaimsChat] = useState(false);
  const [isStartingQuoteChat, setIsStartingQuoteChat] = useState(false);

  useEffect(() => {
    loadFileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  const loadFileData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('📥 Loading file:', fileId);

      const response = await apiService.getUserFile({
        file_id: fileId
      }, user.user_id);

      console.log('✅ File loaded:', response);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load file');
      }

      setFileData(response.data);
    } catch (err) {
      console.error('❌ Load file error:', err);
      setError(err.message || 'Failed to load file');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartPolicyChat = async () => {
    try {
      setIsStartingPolicyChat(true);

      if (fileData.policy_chat_id) {
        console.log('📂 Opening existing policy chat:', fileData.policy_chat_id);
        // ✅ ADD: Navigate with from=policy-details and fileId parameters
        navigate(`/dashboard/chat/${fileData.policy_chat_id}?from=policy-details&fileId=${fileId}`);
        return;
      }

      console.log('🚀 Starting new policy chat for file:', fileId);
      
      const chatResponse = await apiService.startChat({
        agent_name: 'ai_allegoryinsurance_policy_assistant',
        agent_version: 'v1',
        file_id: fileId
      }, user.user_id);

      console.log('✅ Policy chat response:', chatResponse);

      if (!chatResponse.success) {
        throw new Error(chatResponse.error || 'Failed to start chat');
      }

      const newChatId = chatResponse.data.chat_id;
      console.log('✅ Policy chat created:', newChatId);

      setFileData(prev => ({
        ...prev,
        policy_chat_id: newChatId
      }));

      // ✅ ADD: Navigate with from=policy-details and fileId parameters
      navigate(`/dashboard/chat/${newChatId}?from=policy-details&fileId=${fileId}`);

    } catch (err) {
      console.error('❌ Failed to start policy chat:', err);
      alert(err.message || 'Failed to start policy chat');
    } finally {
      setIsStartingPolicyChat(false);
    }
  };

  const handleStartClaimsChat = async () => {
    try {
      setIsStartingClaimsChat(true);

      if (fileData.claims_chat_id) {
        console.log('📂 Opening existing claims chat:', fileData.claims_chat_id);
        // ✅ ADD: Navigate with from=policy-details and fileId parameters
        navigate(`/dashboard/chat/${fileData.claims_chat_id}?from=policy-details&fileId=${fileId}`);
        return;
      }

      console.log('🚀 Starting new claims chat for file:', fileId);
      
      const chatResponse = await apiService.startChat({
        agent_name: 'ai_allegoryinsurance_claims_assistant',
        agent_version: 'v1',
        file_id: fileId
      }, user.user_id);

      console.log('✅ Claims chat response:', chatResponse);

      if (!chatResponse.success) {
        throw new Error(chatResponse.error || 'Failed to start chat');
      }

      const newChatId = chatResponse.data.chat_id;
      console.log('✅ Claims chat created:', newChatId);

      setFileData(prev => ({
        ...prev,
        claims_chat_id: newChatId
      }));

      // ✅ ADD: Navigate with from=policy-details and fileId parameters
      navigate(`/dashboard/chat/${newChatId}?from=policy-details&fileId=${fileId}`);

    } catch (err) {
      console.error('❌ Failed to start claims chat:', err);
      alert(err.message || 'Failed to start claims chat');
    } finally {
      setIsStartingClaimsChat(false);
    }
  };

  const handleStartQuoteChat = async () => {
    try {
      setIsStartingQuoteChat(true);

      if (fileData.quote_chat_id) {
        console.log('📂 Opening existing quote chat:', fileData.quote_chat_id);
        // ✅ ADD: Navigate with from=policy-details and fileId parameters
        navigate(`/dashboard/chat/${fileData.quote_chat_id}?from=policy-details&fileId=${fileId}`);
        return;
      }

      console.log('🚀 Starting new quote chat for file:', fileId);
      
      const chatResponse = await apiService.startChat({
        agent_name: 'ai_allegoryinsurance_quote_assistant',
        agent_version: 'v1',
        file_id: fileId
      }, user.user_id);

      console.log('✅ Quote chat response:', chatResponse);

      if (!chatResponse.success) {
        throw new Error(chatResponse.error || 'Failed to start chat');
      }

      const newChatId = chatResponse.data.chat_id;
      console.log('✅ Quote chat created:', newChatId);

      setFileData(prev => ({
        ...prev,
        quote_chat_id: newChatId
      }));

      // ✅ ADD: Navigate with from=policy-details and fileId parameters
      navigate(`/dashboard/chat/${newChatId}?from=policy-details&fileId=${fileId}`);

    } catch (err) {
      console.error('❌ Failed to start quote chat:', err);
      alert(err.message || 'Failed to start quote chat');
    } finally {
      setIsStartingQuoteChat(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      console.log('🗑️ Deleting file:', fileId);

      const response = await apiService.deleteFile({
        file_id: fileId
      }, user.user_id);

      console.log('✅ Delete response:', response);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete file');
      }

      navigate('/dashboard/policies');
    } catch (err) {
      console.error('❌ Delete file error:', err);
      alert(err.message || 'Failed to delete file');
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader 
          title="Policy Details"
          subtitle="View extracted policy information"
          icon={<FileText size={24} strokeWidth={2} />}
          showBack={true}
          backTo="/dashboard/policies"
        />
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <h2>Failed to Load Policy</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/dashboard/policies')} className={styles.backButton}>
              Back to Policies
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!fileData || !fileData.ai_extracted_metadata) {
    return (
      <PageContainer>
        <PageHeader 
          title="Policy Details"
          subtitle="View extracted policy information"
          icon={<FileText size={24} strokeWidth={2} />}
          showBack={true}
          backTo="/dashboard/policies"
        />
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <FileText size={48} className={styles.errorIcon} />
            <h2>No Data Available</h2>
            <p>This policy hasn't been processed yet</p>
            <button onClick={() => navigate('/dashboard/policies')} className={styles.backButton}>
              Back to Policies
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader 
        // title={fileData.file_name}
        title="Your Insurance Policy"
        subtitle="View extracted policy information and manage policy"
        icon={<FileText size={24} strokeWidth={2} />}
        showBack={true}
        backTo="/dashboard/policies"
      />

      <PolicyCommandCenter 
        metadata={fileData.ai_extracted_metadata}
        fileInfo={fileData}
        onStartPolicyChat={handleStartPolicyChat}
        onStartClaimsChat={handleStartClaimsChat}
        onStartQuoteChat={handleStartQuoteChat}
        onDelete={() => setShowDeleteModal(true)}
        isStartingPolicyChat={isStartingPolicyChat}
        isStartingClaimsChat={isStartingClaimsChat}
        isStartingQuoteChat={isStartingQuoteChat}
      />

      {showDeleteModal && (
        <div className={styles.modalBackdrop} onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Delete Policy?</h3>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalMessage}>
                Are you sure you want to delete <strong>"{fileData.file_name}"</strong>? This action cannot be undone.
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button 
                onClick={() => setShowDeleteModal(false)} 
                className={styles.cancelButton}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete} 
                className={styles.confirmDeleteButton}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={18} strokeWidth={2} className={styles.spinning} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} strokeWidth={2} />
                    Delete Policy
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default PolicyDetails;